// Entry-chunk-safe language core. This module MUST stay dependency-free:
// it is imported by main.jsx/App.jsx, which live inside the 147 KB gz landing
// entry budget. i18next itself loads only in lazy route chunks (src/i18n/index.js).
const STORAGE_KEY = 'mz_lang';
const HINT_KEY = 'mz_lang_hint_dismissed';
const NUDGE_KEY = 'mz_lang_nudge_seen';

// Languages the UI can render. Expand as locale files ship (keep in sync with
// the backend GeoIPService SUPPORTED_LANGUAGES and src/i18n/index.js).
export const SUPPORTED_LANGS = ['hi', 'en', 'ta'];
// URL/SEO bare-root default (x-default). Hindi pages live at `/` (unprefixed);
// drives buildLangPath / resolveLangFromUrl / prerender. Kept as 'hi' so the
// public SEO URL structure is unchanged.
export const DEFAULT_LANG = 'hi';
// App-UI default language for a visitor with no saved choice and no URL prefix.
// Decoupled from DEFAULT_LANG (2026-06-22): the app now opens in English by
// default while Hindi/Tamil remain available via the toggle and keep their SEO URLs.
export const UI_DEFAULT_LANG = 'en';

// Native + English label for each language, used by the LanguagePicker.
// `nativeShort` is the compact label shown on the always-visible trigger.
export const LANG_LABELS = {
  hi: { native: 'हिंदी', english: 'Hindi', nativeShort: 'हिं' },
  en: { native: 'English', english: 'English', nativeShort: 'EN' },
  ta: { native: 'தமிழ்', english: 'Tamil', nativeShort: 'தமிழ்' },
};

// Languages that get a URL path prefix for SEO (crawlable, hreflang-linked
// variants). Hindi is the default and lives at the bare root `/` (= x-default),
// so it is NOT prefixed. Keep in sync with scripts/prerender-meta.mjs.
export const PREFIX_LANGS = ['en', 'ta'];

/** Is `lang` a code we can render? */
export function isSupportedLang(lang) {
  return SUPPORTED_LANGS.includes(lang);
}

/**
 * Inspect the current URL for a leading language segment (`/en/...`, `/ta/...`).
 * Entry-safe (no i18next). Returns the router basename to mount and the URL
 * language, or empty/null when the path is unprefixed (Hindi root).
 * @returns {{ basename: string, urlLang: string|null }}
 */
export function resolveLangFromUrl() {
  if (typeof window === 'undefined') return { basename: '', urlLang: null };
  const seg = window.location.pathname.split('/')[1];
  if (PREFIX_LANGS.includes(seg)) return { basename: `/${seg}`, urlLang: seg };
  return { basename: '', urlLang: null };
}

/**
 * Build the path for `targetLang` from a (possibly already-prefixed) pathname.
 * Hindi → bare path; others → `/{lang}` prefix. Used for hreflang links and any
 * explicit language navigation.
 * @param {string} targetLang
 * @param {string} pathname
 * @returns {string}
 */
export function buildLangPath(targetLang, pathname) {
  const parts = pathname.split('/');
  // Strip an existing language prefix if present.
  if (PREFIX_LANGS.includes(parts[1])) parts.splice(1, 1);
  const bare = parts.join('/') || '/';
  if (targetLang === DEFAULT_LANG) return bare;
  return `/${targetLang}${bare === '/' ? '' : bare}` || `/${targetLang}`;
}

/** Raw stored choice, or null when the visitor has never picked a language. */
export function getStoredLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGS.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Active app-UI language: the visitor's stored choice, else English (the app
 *  default). NOTE: this is UI_DEFAULT_LANG, not DEFAULT_LANG — the bare-root SEO
 *  language stays Hindi, but the app opens in English when nothing is stored. */
export function getLang() {
  return getStoredLang() || UI_DEFAULT_LANG;
}

export function setLang(lang) {
  if (!SUPPORTED_LANGS.includes(lang)) return;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // storage blocked (private mode) — language still applies for this page life
  }
  applyLangAttribute(lang);
}

/** Keeps <html lang> in sync for screen readers, hyphenation and crawlers. */
export function applyLangAttribute(lang = getLang()) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
}

// --- First-visit hint (legacy LanguageToggle bubble) ---

export function isHintDismissed() {
  try {
    return localStorage.getItem(HINT_KEY) === '1';
  } catch {
    return true;
  }
}

export function dismissHint() {
  try {
    localStorage.setItem(HINT_KEY, '1');
  } catch {
    // non-fatal
  }
}

// --- First-visit IP-detection nudge (LanguageNudge) ---

/** Has the IP-detection nudge already been shown/dismissed for this browser? */
export function isNudgeSeen() {
  try {
    return localStorage.getItem(NUDGE_KEY) === '1';
  } catch {
    return true;
  }
}

export function markNudgeSeen() {
  try {
    localStorage.setItem(NUDGE_KEY, '1');
  } catch {
    // non-fatal
  }
}
